import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  POST_COLLECTION_YARD_VISITS_LIST_URL,
  UPDATE_IS_PAID_STATUS_COLLECTION_YARD_VISITS_URL,
} from "../middleware/routes";
import { RootState } from "../store";
import {
  ICollectionYardsState,
  ICollectionYardsVisit,
  IUpdateVisitIsPaidStatusRequest,
  IUpdateVisitIsPaidStatusResponse,
} from "../types/collectionYards";
import { IFilterItem, IFilters } from "../types/filters";
import { IOrdering, IPagination } from "../types/pagination";
import { ISearch } from "../types/search";
import { Status } from "../types/state";
import { axiosRequestApi } from "../utils/axiosRequest";
import { getSkeletonMockedArray } from "../utils/tables/getSkeletonMockedArray";
import { getSkeletonSuccessArray } from "../utils/tables/getSkeletonSuccessArray";
import { adaptRequestBodyForBackend, adaptResponseForFronted } from "./utils/collectionYardsAdapt";
import { filterOutUndefinedFilters } from "./utils/filters";

export const defaultPagination = {
  page: 1,
  limit: 50,
};

export const defaultOrdering = {
  orderBy: "created",
  descending: true,
};

export const defaultSearch = {
  fulltext: "",
};

const defaultVisit: ICollectionYardsVisit = {
  visitId: 0,
  locationId: 0,
  wasteTypes: [],
  wasteSum: 0,
  priceSum: 0,
  paidAt: null,
  created: "",
  locationName: "",
  collectionYardName: "",
  wcpid: null,
};

const defaultVisits: ICollectionYardsVisit[] = getSkeletonMockedArray(defaultVisit, 50, 0);

const defaultFilters: IFilters = {
  items: [],
  linkOperator: "and",
};

const initCollectionYardsVisitsDetailState: ICollectionYardsState = {
  requestId: "",
  status: Status.idle,
  errorMsg: null,
  visits: defaultVisits,
  isNextPageEnable: false,
  pagination: defaultPagination,
  ordering: defaultOrdering,
  filters: defaultFilters,
  search: defaultSearch,
  scrollTop: 0,
};

export const collectionYardsVisitsDetailSlice = createSlice({
  name: "collectionYardsVisitsDetail",
  initialState: initCollectionYardsVisitsDetailState,
  reducers: {
    cleanUpCollectionYardsVisitsDetail: (state: ICollectionYardsState) => {
      state.status = Status.success;
      state.errorMsg = null;
      state.visits = defaultVisits;
      state.isNextPageEnable = false;
    },
    replaceCollectionYardsVisitsDetail: (
      state: ICollectionYardsState,
      action: PayloadAction<ICollectionYardsVisit>
    ) => {
      const visitsPoints = [...state.visits];
      const newVisits = visitsPoints.map((visit) => {
        if (visit.visitId === action.payload.visitId) {
          return action.payload;
        }
        return visit;
      });

      state.visits = newVisits;
    },
    setCollectionYardsVisitsDetailPagination: (state: ICollectionYardsState, action: PayloadAction<IPagination>) => {
      state.pagination = action.payload;
    },
    setCollectionYardsVisitsDetailOrdering: (state: ICollectionYardsState, action: PayloadAction<IOrdering>) => {
      state.ordering = action.payload;
    },
    setCollectionYardsVisitsDetailFilter: (state, action: PayloadAction<IFilterItem>) => {
      const findIdx = state.filters.items.findIndex((item) => item.id === action.payload.id);
      if (findIdx === -1) {
        state.filters.items.push(action.payload);
        return;
      }

      state.filters.items[findIdx] = action.payload;
      state.filters.items = filterOutUndefinedFilters(state.filters.items, action.payload);
    },
    removeCollectionYardsVisitsDetailFilter: (state, action: PayloadAction<string>) => {
      state.filters.items = state.filters.items.filter((item) => !item.id?.toString().startsWith(action.payload));
    },
    setCollectionYardsVisitsDetailSearch: (state: ICollectionYardsState, action: PayloadAction<ISearch>) => {
      state.search = action.payload;
    },
    setCollectionYardsVisitsDetailScrollTop: (state: ICollectionYardsState, action: PayloadAction<number>) => {
      state.scrollTop = action.payload;
    },
    resetCollectionYardsVisitsDetailState: () => {
      return initCollectionYardsVisitsDetailState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCollectionYardsVisitsDetailThunk.pending, (state, action) => {
        state.status = Status.requesting;
        state.requestId = action.meta.requestId;

        if (state.pagination.page > 1) {
          const mockedContainers = getSkeletonMockedArray(defaultVisits, 3, state.visits.length);
          state.visits = [...state.visits, ...mockedContainers];
        }
      })
      .addCase(getCollectionYardsVisitsDetailThunk.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) {
          return;
        }

        const visitsFromState: ICollectionYardsVisit[] = state.visits;
        const visitsFromResponse: ICollectionYardsVisit[] = action.payload;
        const newVisits = getSkeletonSuccessArray(visitsFromState, visitsFromResponse);

        state.visits = newVisits;
        state.isNextPageEnable = action.payload.length >= state.pagination.limit;
        state.errorMsg = null;
        state.status = Status.success;
      })
      .addCase(getCollectionYardsVisitsDetailThunk.rejected, (state) => {
        state.status = Status.error;
        state.errorMsg = "there has been an error";
      })
      .addCase(updateYardVisitIsPaidStatusThunk.fulfilled, (state, action) => {
        const { collection_yard_visit_id, paid_at } = action.payload;

        state.visits = state.visits.map((visit) =>
          visit.visitId === collection_yard_visit_id
            ? {
                ...visit,
                paidAt: paid_at,
              }
            : visit
        );
      });
  },
});

export const getCollectionYardsVisitsDetailThunk = createAsyncThunk<any, number, { state: RootState }>(
  "collectionYardsVisitsDetail/getCollectionYardsVisitsDetailThunk",
  async (locationId, { getState }) => {
    const state = getState();
    const year = Number(state.application.years.selectedYear.value);

    const requestObject = adaptRequestBodyForBackend(state.collectionYardsVisitsDetail, year, locationId);
    const response = await axiosRequestApi.post(POST_COLLECTION_YARD_VISITS_LIST_URL, requestObject);
    return adaptResponseForFronted(response.data);
  }
);

export const updateYardVisitIsPaidStatusThunk = createAsyncThunk<
  IUpdateVisitIsPaidStatusResponse,
  IUpdateVisitIsPaidStatusRequest
>("collectionYardsVisitsDetail/updateYardVisitIsPaidStatusThunk", async (updateRequest) => {
  const response = await axiosRequestApi.post(UPDATE_IS_PAID_STATUS_COLLECTION_YARD_VISITS_URL, updateRequest);

  return response.data;
});

export const {
  cleanUpCollectionYardsVisitsDetail,
  setCollectionYardsVisitsDetailPagination,
  setCollectionYardsVisitsDetailOrdering,
  setCollectionYardsVisitsDetailSearch,
  setCollectionYardsVisitsDetailScrollTop,
  replaceCollectionYardsVisitsDetail,
  resetCollectionYardsVisitsDetailState,
  setCollectionYardsVisitsDetailFilter,
  removeCollectionYardsVisitsDetailFilter,
} = collectionYardsVisitsDetailSlice.actions;

export const collectionYardsVisitsDetailReducer = collectionYardsVisitsDetailSlice.reducer;
